import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LogbookStats, LogbookReportDates } from '../models/logbook.model';
import { OrganizationContextService } from './organization-context.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Logbook Overview Service
 *
 * Fetches logbook stats (total acres sprayed) and report last-generated dates.
 * Uses signal-based state management matching DashboardService patterns.
 *
 * Reference: /agent-os/specs/2026-01-27-logbook-overview-page/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class LogbookOverviewService {
  private http = inject(HttpClient);
  private organizationContext = inject(OrganizationContextService);

  private apiUrl = environment.apiUrl;

  // Signal-based state management
  private statsSignal = signal<LogbookStats | null>(null);
  private reportDatesSignal = signal<LogbookReportDates | null>(null);
  private loadingStatsSignal = signal<boolean>(false);
  private loadingReportDatesSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly stats$ = computed(() => this.statsSignal());
  readonly reportDates$ = computed(() => this.reportDatesSignal());
  readonly isLoadingStats$ = computed(() => this.loadingStatsSignal());
  readonly isLoadingReportDates$ = computed(() => this.loadingReportDatesSignal());
  readonly isLoading$ = computed(() => this.loadingStatsSignal() || this.loadingReportDatesSignal());
  readonly error$ = computed(() => this.errorSignal());

  /**
   * Load logbook stats (total acres sprayed)
   */
  async loadStats(): Promise<LogbookStats | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[LogbookOverviewService] No organization selected');
      return null;
    }

    this.loadingStatsSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<LogbookStats>>(
          `${this.apiUrl}/organizations/${orgId}/logbook/stats`
        )
      );

      if (response.success && response.data) {
        this.statsSignal.set(response.data);
        return response.data;
      }

      throw new Error(response.error || 'Failed to load logbook stats');
    } catch (error: any) {
      console.error('[LogbookOverviewService] Error loading stats:', error);
      this.errorSignal.set(error.message || 'Failed to load logbook stats');
      return null;
    } finally {
      this.loadingStatsSignal.set(false);
    }
  }

  /**
   * Load report last-generated dates
   */
  async loadReportDates(): Promise<LogbookReportDates | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[LogbookOverviewService] No organization selected');
      return null;
    }

    this.loadingReportDatesSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<LogbookReportDates>>(
          `${this.apiUrl}/organizations/${orgId}/logbook/report-dates`
        )
      );

      if (response.success && response.data) {
        this.reportDatesSignal.set(response.data);
        return response.data;
      }

      throw new Error(response.error || 'Failed to load report dates');
    } catch (error: any) {
      console.error('[LogbookOverviewService] Error loading report dates:', error);
      this.errorSignal.set(error.message || 'Failed to load report dates');
      return null;
    } finally {
      this.loadingReportDatesSignal.set(false);
    }
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
